/**
 * Sprint 24.13 (S24.13.10) — per-turn prompt-cache hit indicator.
 *
 * A single inline micro-stat for the CostFooter: `🔁 N% cached`, colour-banded
 * by how much of the latest turn's input was served from Anthropic's prompt
 * cache (cheaper). NOT a session aggregate — it reflects the most recent turn,
 * because that's the actionable signal (a scope change or new conversation
 * resets the cache, and the user sees the ratio recover next turn).
 *
 * First turn: cache is priming (writes dominate, zero reads) so a ratio would
 * be misleadingly ~0% — show `🔁 —` instead.
 *
 * Pure presentational component (all inputs are props) so every state — three
 * colour bands + the priming dash — is trivially renderable for the
 * [[feedback-chrome-spec-visual-checklist]] six-check. Uses existing
 * --color-{success,warning,error} tokens (light + dark covered by the tokens).
 */

import type { CSSProperties } from "react";

interface CacheHitIndicatorProps {
  /** Latest turn's tokens served from the prompt cache (cache_read). */
  cachedTokens: number;
  /** Latest turn's total input tokens (cached reads + cache writes + uncached). */
  totalInputTokens: number;
}

const BAND_HIGH = "var(--color-success)";
const BAND_MID = "var(--color-warning)";
const BAND_LOW = "var(--color-error)";

export function CacheHitIndicator({ cachedTokens, totalInputTokens }: CacheHitIndicatorProps) {
  // Priming / first turn: no tokens have been served from cache yet. Showing a
  // ratio here would read as a misleading 0%.
  const priming = cachedTokens <= 0 || totalInputTokens <= 0;

  if (priming) {
    return (
      <span
        style={dash}
        title="First turn — cache priming. Next turn will show the ratio."
      >
        🔁 —
      </span>
    );
  }

  const ratio = Math.max(0, Math.min(1, cachedTokens / totalInputTokens));
  const pct = Math.round(ratio * 100);
  const color = ratio >= 0.8 ? BAND_HIGH : ratio >= 0.4 ? BAND_MID : BAND_LOW;

  return (
    <span
      style={{ ...indicator, color }}
      title={`${cachedTokens.toLocaleString()} / ${totalInputTokens.toLocaleString()} input tokens cached this turn. Higher is cheaper. New conversations and scope changes reset the cache.`}
    >
      🔁 {pct}% cached
    </span>
  );
}

const indicator: CSSProperties = {
  fontVariantNumeric: "tabular-nums",
  fontWeight: 500,
};

const dash: CSSProperties = {
  fontVariantNumeric: "tabular-nums",
  opacity: 0.7,
};
